import type { LiveComposerEvent } from '../../CreateMusicXml';
import type { PlaybackInstruction } from './playback';
import { isTakeNoteUncertain, type PerformanceNote, type PerformanceTake } from './performance';

const DIVISIONS_PER_BEAT = 4;
const SUSTAIN_PEDAL_CONTROLLER = 64;
const SUSTAIN_PEDAL_ON_VALUE = 64;

/** One division is a sixteenth, so a quarter note spans four of them. */
export const getDivisionDurationMs = (tempoBpm: number) =>
  60000 / Math.max(1, tempoBpm) / DIVISIONS_PER_BEAT;

/**
 * Where a recorded observation falls in the written score. The take keeps its own
 * clock, so review can jump from "this note sounded wrong" to the engraved bar.
 */
export const takeTimeToDivisions = (take: PerformanceTake, atMs: number) =>
  take.startDivisions + atMs / getDivisionDurationMs(take.tempo);

export type TakeReviewNote = {
  note: PerformanceNote;
  startDivisions: number;
  endDivisions: number;
  /** The engraved event that came from this observation, when it still exists. */
  eventId?: string;
};

export type TakeSummary = {
  id: string;
  name: string;
  source: PerformanceTake['source'];
  createdAt: string;
  durationMs: number;
  startDivisions: number;
  noteCount: number;
  reviewCount: number;
  status: PerformanceTake['status'];
  hasAudio: boolean;
  audioByteLength: number;
};

export function summarizeTake(take: PerformanceTake): TakeSummary {
  return {
    id: take.id,
    name: take.name,
    source: take.source,
    createdAt: take.createdAt,
    durationMs: take.durationMs,
    startDivisions: take.startDivisions,
    noteCount: take.notes.length,
    reviewCount: take.notes.filter(isTakeNoteUncertain).length,
    status: take.status,
    hasAudio: Boolean(take.audio),
    audioByteLength: take.audio?.byteLength ?? 0,
  };
}

/**
 * The observations a musician should check, earliest first. Only notes the detector
 * was unsure about qualify: a MIDI take reports no uncertainty because the
 * instrument stated the pitch, and inventing doubt there would waste review time.
 */
export function getTakeReviewNotes(
  take: PerformanceTake,
  events: LiveComposerEvent[] = [],
): TakeReviewNote[] {
  const eventByNoteId = new Map<string, string>();
  for (const event of events)
    for (const noteId of event.performanceNoteIds ?? []) eventByNoteId.set(noteId, event.id);
  return take.notes
    .filter(isTakeNoteUncertain)
    .slice()
    .sort((left, right) => left.startedAtMs - right.startedAtMs)
    .map((note) => ({
      note,
      startDivisions: takeTimeToDivisions(take, note.startedAtMs),
      endDivisions: takeTimeToDivisions(take, note.endedAtMs),
      eventId: eventByNoteId.get(note.id),
    }));
}

/** Engraved events that carry at least one observation the detector doubted. */
export function getEventIdsNeedingReview(
  takes: PerformanceTake[],
  events: LiveComposerEvent[],
): Set<string> {
  const uncertainNoteIds = new Set<string>();
  for (const take of takes)
    for (const note of take.notes) if (isTakeNoteUncertain(note)) uncertainNoteIds.add(note.id);
  const flagged = new Set<string>();
  if (uncertainNoteIds.size === 0) return flagged;
  for (const event of events)
    if ((event.performanceNoteIds ?? []).some((id) => uncertainNoteIds.has(id)))
      flagged.add(event.id);
  return flagged;
}

/**
 * Replays the take exactly as it was performed, in the same instruction form the
 * score uses. Comparing "what I played" with "what was written" then runs through
 * one scheduler instead of two, so the two readings cannot drift apart.
 *
 * Positions are expressed against `playbackTempoBpm`, the tempo the scheduler will
 * use to turn divisions back into milliseconds. Using the take's own tempo here
 * would stretch the recording whenever the score tempo has since changed, and the
 * point of an original performance is that it is not re-interpreted.
 */
export function buildTakePlaybackWindow(
  take: PerformanceTake,
  playbackTempoBpm: number,
): PlaybackInstruction[] {
  const atDivisions = (atMs: number) =>
    take.startDivisions + atMs / getDivisionDurationMs(playbackTempoBpm);
  const instructions: PlaybackInstruction[] = [];
  for (const note of take.notes) {
    const velocities = note.velocity === null ? undefined : { [note.midi]: note.velocity };
    instructions.push({
      id: `${note.id}:on`,
      atDivisions: atDivisions(note.startedAtMs),
      type: 'note-on',
      midiNotes: [note.midi],
      source: take.source,
      ...(velocities ? { velocities } : {}),
    });
    instructions.push({
      id: `${note.id}:off`,
      atDivisions: atDivisions(note.endedAtMs),
      type: 'note-off',
      midiNotes: [note.midi],
      source: take.source,
    });
  }
  let pedalDown = false;
  for (const controller of take.controllers) {
    if (controller.controller !== SUSTAIN_PEDAL_CONTROLLER) continue;
    const down = controller.value >= SUSTAIN_PEDAL_ON_VALUE;
    if (down === pedalDown) continue;
    pedalDown = down;
    instructions.push({
      id: `${take.id}:pedal:${controller.atMs}`,
      atDivisions: atDivisions(controller.atMs),
      type: 'pedal',
      pedalDown: down,
    });
  }
  const priority = (instruction: PlaybackInstruction) =>
    instruction.type === 'pedal'
      ? 0
      : instruction.type === 'note-off'
        ? 1
        : instruction.type === 'note-on'
          ? 2
          : 4;
  instructions.sort(
    (left, right) => left.atDivisions - right.atDivisions || priority(left) - priority(right),
  );
  instructions.push({
    id: `${take.id}:end`,
    atDivisions: atDivisions(take.durationMs),
    type: 'end',
  });
  return instructions;
}

/**
 * The passage a take occupies, for replacing it with a new performance. Returns null
 * for a take that produced no sound, which has no span to replace.
 */
export function getTakeRange(take: PerformanceTake) {
  if (take.notes.length === 0) return null;
  const endMs = take.notes.reduce((latest, note) => Math.max(latest, note.endedAtMs), 0);
  const startDivisions = take.startDivisions;
  const endDivisions = takeTimeToDivisions(take, endMs);
  if (endDivisions <= startDivisions) return null;
  return { startDivisions, endDivisions };
}

const MIDI_NAMES = ['C', 'C♯', 'D', 'E♭', 'E', 'F', 'F♯', 'G', 'A♭', 'A', 'B♭', 'B'];

/** A pitch name a musician can read, e.g. 64 becomes E4. */
export const describeMidiNote = (midi: number) =>
  `${MIDI_NAMES[((midi % 12) + 12) % 12]}${Math.floor(midi / 12) - 1}`;
